import { BusinessMarker } from 'selectors/availabilitySearchSelectors';
import { core } from '@tock/coretypes';
import { getCityLatLong } from 'utils/locationUtils';
import _, { memoize } from 'lodash';

// The minimum amount of pins we are going to contain in our zoom.
export const MIN_PIN_COUNT = 8;

/**
 * Shared imperative map helpers used by both the legacy `ConsumerMap`
 * (`@react-google-maps/api`) and the vis.gl `ConsumerMapNew`. Extracting them
 * keeps the two code paths in exact behavioral lockstep during the migration.
 */
export async function fitMapToLocations(
  map: google.maps.Map,
  locations: google.maps.LatLngLiteral[],
  metroArea?: core.MetroArea,
  locationsSortedGeographically?: boolean
) {
  let boundLocations = [...locations];
  if (locationsSortedGeographically && locations.length) {
    boundLocations = boundLocations.slice(0, MIN_PIN_COUNT);
  }

  const boundingBox = new google.maps.LatLngBounds();
  if (metroArea && boundLocations.length === 0) {
    try {
      const geocodedZip = await getCityLatLong(metroArea.name, metroArea.country);
      const viewport = geocodedZip.geometry.viewport;
      if (_.isNumber(viewport.getNorthEast().lat)) {
        boundingBox.extend(viewport.getNorthEast());
        boundingBox.extend(viewport.getSouthWest());
      } else {
        boundingBox.extend(
          new google.maps.LatLng(viewport.getNorthEast().lat(), viewport.getNorthEast().lng())
        );
        boundingBox.extend(
          new google.maps.LatLng(viewport.getSouthWest().lat(), viewport.getSouthWest().lng())
        );
      }
      map.fitBounds(boundingBox);
      return;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      // continue to the next attempt
    }
  }

  if (!metroArea && _.isEmpty(boundLocations)) {
    // Else manually center around the united states
    boundingBox.extend(new google.maps.LatLng(50, -125));
    boundingBox.extend(new google.maps.LatLng(30, -67.5));
    map.fitBounds(boundingBox);
    return;
  }

  const expandBoundingBox = (location: google.maps.LatLngLiteral) => {
    const { lat, lng } = location;
    // 0.01 delta in lat and lng is approx 1km hypotenuse
    // For each location cover atleast a 2 km bounding box.
    boundingBox.extend(new google.maps.LatLng(lat + 0.01, lng + 0.01));
    boundingBox.extend(new google.maps.LatLng(lat - 0.01, lng - 0.01));
  };

  if (boundLocations.length === 1) {
    expandBoundingBox(boundLocations[0]);
    map.fitBounds(boundingBox);
    return;
  }

  // Server already sorted for us in this case. No geometry needed!
  if (locationsSortedGeographically) {
    boundLocations.forEach(expandBoundingBox);
  } else {
    const centroid: google.maps.LatLngLiteral = { lat: 0, lng: 0 };

    boundLocations.forEach((location) => {
      centroid.lat += location.lat;
      centroid.lng += location.lng;
    });
    centroid.lat /= boundLocations.length;
    centroid.lng /= boundLocations.length;

    // turning lat and lng into a string key for memoizing purposes
    const latLngKey = ({ lat, lng }: google.maps.LatLngLiteral) => `${lat}-${lng}`;

    const distanceBetweenLatLngs = memoize(
      (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number =>
        Math.sqrt(Math.pow(a.lat - b.lat, 2) + Math.pow(a.lng - b.lng, 2)),
      (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) =>
        `${latLngKey(a)}-${latLngKey(b)}`
    );

    boundLocations.sort((a, b) => {
      const distanceA = distanceBetweenLatLngs(a, centroid);
      const distanceB = distanceBetweenLatLngs(b, centroid);
      return distanceA - distanceB;
    });

    // Compute the 98th percentile entry and compute bounding box based items within that.
    for (let i = 0; i < Math.floor(boundLocations.length * 0.98); i++) {
      expandBoundingBox(boundLocations[i]);
    }
  }

  map.fitBounds(boundingBox);
}

export const panMapToLocation = (map: google.maps.Map, location: google.maps.LatLngLiteral) => {
  map.panTo(location);
};

export const findMarkerForBusiness = (markers: BusinessMarker[], selectedBusinessId: number) => {
  return markers.find((marker) => marker.id === selectedBusinessId);
};
